import { useQueries } from '@tanstack/react-query';
import { useMemo } from 'react';

import type { getOrpc } from '@/_shared/lib/orpc';

import type { DashboardParam } from '../types';

interface ParamOption {
  label: string;
  value: string;
}

/**
 * Fetches dynamic options for dashboard parameters that have a `sql` field.
 * Returns merged parameters with fetched options replacing/supplementing static ones.
 *
 * Merge rule: only replace `options` when fetched result is non-empty.
 * If SQL returns 0 rows, preserve the static `options` fallback.
 *
 * Accepts an `orpc` instance so both workspace-scoped and public routes
 * can share this hook with their own oRPC utils.
 */

export function useDynamicParamOptions(
  dashboardId: string | undefined,
  parameters: DashboardParam[],
  orpc: ReturnType<typeof getOrpc>,
  shareToken?: string,
): { parameters: DashboardParam[]; isLoading: boolean; loadingParamNames: Set<string> } {
  // Either flag signals "this param has dynamic options". Owner views populate
  // `sql` (raw SQL); the public projection redacts SQL and sets
  // `hasDynamicOptions` instead. Both routes call paramOptions, which is the
  // server-side endpoint that knows the real SQL.
  const dynamicParams = useMemo(() => parameters.filter((p) => !!p.sql || p.hasDynamicOptions === true), [parameters]);

  const queries = useQueries({
    queries: dynamicParams.map((p) => ({
      ...orpc.dashboard.paramOptions.queryOptions({
        // shareToken is required for anonymous /pub/<token> traffic — the
        // backend rejects anonymous calls without a token that resolves to
        // this dashboard. Owner views pass undefined and bypass the check.
        input: { dashboardId: dashboardId ?? '', paramName: p.name, ...(shareToken ? { shareToken } : {}) },
      }),
      enabled: !!dashboardId,
      staleTime: 5 * 60 * 1000, // 5 min cache
    })),
  });

  const isLoading = queries.some((q) => q.isLoading);

  const loadingParamNames = useMemo(() => {
    const names = new Set<string>();
    for (let i = 0; i < dynamicParams.length; i++) {
      if (queries[i]?.isLoading) {
        names.add(dynamicParams[i]!.name);
      }
    }
    return names;
  }, [dynamicParams, queries]);

  const resolvedParams = useMemo(() => {
    if (dynamicParams.length === 0) return parameters;

    // Build a map of fetched options by param name
    const fetchedMap = new Map<string, ParamOption[]>();
    for (let i = 0; i < dynamicParams.length; i++) {
      const query = queries[i];
      if (query?.data?.options && query.data.options.length > 0) {
        fetchedMap.set(dynamicParams[i]!.name, query.data.options);
      }
    }

    return parameters.map((p) => {
      const fetched = fetchedMap.get(p.name);
      if (fetched) {
        return { ...p, options: fetched };
      }
      return p;
    });
  }, [parameters, dynamicParams, queries]);

  return { parameters: resolvedParams, isLoading, loadingParamNames };
}
